import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search, Sparkles, Trash2, Wand2 } from 'lucide-react';
import * as api from '../../api';
import { Btn, HeaderBack, Input, RNLLoader, SearchableSelect, Select, Textarea } from '../UI';
import Pagination from '../requisition/Pagination';
import { CommentModal, Field, Panel } from '../materialTakeoff/MTOShared';
import { StatusPill } from './MMRegister';
import { COVERAGE, EDITABLE, MAPPING_TYPES, asOptions, autoClassify, fmtDate, fmtQty } from './mmUtils';

const th = { padding: '9px 10px', fontSize: 9.5, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left' };
const td = { padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' };

const EVENT_LABEL = { submitted: 'Submitted', resubmitted: 'Resubmitted', approved: 'Approved', step_approved: 'Approved at step',
                      completed: 'Fully approved', returned: 'Returned', rejected: 'Rejected', recalled: 'Recalled', cancelled: 'Cancelled' };

// One BOQ line → the materials that execute it. Create, change, send for approval, and (for approvers) decide.
export default function MMEditor({ projectId, lists, boqItemId, materialId, goTo, openMapper, canEdit, toast }) {
  const [mapping, setMapping] = useState(undefined);       // undefined = loading, null = not mapped yet
  const [line, setLine] = useState(null);                  // the BOQ line (from the register)
  const [selected, setSelected] = useState([]);            // [{ material_id, code, name, category, uom, mapping_type, boq_coverage, quantity_per_unit, remarks }]
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [target, setTarget] = useState({ division: '', category: '', subsection: '' });
  const [boqOptions, setBoqOptions] = useState([]);
  const [busy, setBusy] = useState('');
  const [canApprove, setCanApprove] = useState(false);
  const [modal, setModal] = useState(null);                // 'return' | 'reject' | 'approve'
  // material picker
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 25 });
  const [materials, setMaterials] = useState(null);
  const [matTotal, setMatTotal] = useState(0);

  const fromMapping = (m) => {
    setMapping(m);
    setNotes(m?.notes || '');
    setSelected((m?.lines || []).map(x => ({
      material_id: x.material_id, code: x.material_code, name: x.material_name, category: x.material_category, uom: x.material_uom,
      mapping_type: x.mapping_type, boq_coverage: x.boq_coverage, quantity_per_unit: x.quantity_per_unit ?? '', remarks: x.remarks || '',
    })));
    setDirty(false);
  };

  // the mapping (if any), the BOQ line, suggestions, and whether it waits at my approval step
  const load = useCallback(async () => {
    api.clearApiCache();
    const [m, reg, sug] = await Promise.all([
      api.getMappingForBoqLine(boqItemId).catch(() => null),
      api.listMappingRegister({ project_id: projectId, boq_item_id: boqItemId, limit: 1 }).catch(() => []),
      api.getMappingSuggestions(boqItemId).catch(() => []),
    ]);
    fromMapping(m || null);
    setSuggestions(sug || []);
    setLine(m?.boq_line || (reg || [])[0] || null);
    if (m?.status === 'pending_approval') {
      const inbox = await api.getMyInbox().catch(() => []);
      setCanApprove((inbox || []).some(i => i.entity_type === 'boq_material_mapping' && i.entity_id === m.id));
    } else setCanApprove(false);
  }, [boqItemId, projectId]);

  useEffect(() => { load(); }, [load]);

  // the target selectors start from the open line
  useEffect(() => {
    if (line) setTarget({ division: line.project_division_id || '', category: line.dmr_category_id || '', subsection: line.dmr_subsection_id || '' });
  }, [line]);

  // BOQ lines for the target selectors
  useEffect(() => {
    if (!projectId) return;
    api.listMappingRegister({ project_id: projectId, project_division_id: target.division, dmr_category_id: target.category,
                              dmr_subsection_id: target.subsection, limit: 2000 })
      .then(r => setBoqOptions(r || [])).catch(() => setBoqOptions([]));
  }, [projectId, target]);

  // a material handed over from Unmapped Materials
  useEffect(() => {
    if (!materialId || mapping === undefined) return;
    if (selected.some(s => s.material_id === materialId)) return;
    api.getMaterial(materialId).then(m => m && addMaterial(m)).catch(() => {});
  }, [materialId, mapping]); // eslint-disable-line react-hooks/exhaustive-deps

  // the material master, a page at a time
  useEffect(() => {
    setMaterials(null);
    api.listMaterials(page.skip, page.limit, query || catFilter)
      .then(r => { setMaterials(r || []); setMatTotal(r?.totalCount ?? (r || []).length); })
      .catch(() => { setMaterials([]); setMatTotal(0); });
  }, [page, query, catFilter]);

  const editable = canEdit && (!mapping || EDITABLE.includes(mapping.status));
  const selectedIds = useMemo(() => new Set(selected.map(s => s.material_id)), [selected]);
  const categories = useMemo(() => [...new Set((materials || []).map(m => m.category).filter(Boolean))].sort(), [materials]);

  function addMaterial(m) {
    const id = m.material_id || m.id;
    if (selectedIds.has(id)) return;
    const auto = autoClassify(m.name, line?.description || '');
    setSelected(prev => [...prev, { material_id: id, code: m.code, name: m.name, category: m.category, uom: m.unit_of_measure,
                                    ...auto, quantity_per_unit: '', remarks: '' }]);
    setDirty(true);
  }
  const toggleMaterial = (m) => {
    const id = m.material_id || m.id;
    if (selectedIds.has(id)) { setSelected(prev => prev.filter(s => s.material_id !== id)); setDirty(true); } else addMaterial(m);
  };
  const setRow = (id, k, v) => { setSelected(prev => prev.map(s => (s.material_id === id ? { ...s, [k]: v } : s))); setDirty(true); };
  const autoAll = () => { setSelected(prev => prev.map(s => ({ ...s, ...autoClassify(s.name, line?.description || '') }))); setDirty(true); };

  const payloadLines = () => selected.map(s => ({
    material_id: s.material_id, mapping_type: s.mapping_type, boq_coverage: s.boq_coverage,
    quantity_per_unit: s.quantity_per_unit === '' || s.quantity_per_unit == null ? null : Number(s.quantity_per_unit),
    remarks: s.remarks || null,
  }));

  const run = async (label, fn, okMsg) => {
    setBusy(label);
    try {
      const r = await fn();
      api.clearApiCache();
      toast?.show?.(r?.message || okMsg, 'success');
      await load();
      return true;
    } catch (e) {
      toast?.show?.(e.message || 'Something went wrong', 'error');
      return false;
    } finally {
      setBusy('');
    }
  };

  const save = () => {
    if (!selected.length) { toast?.show?.('Select at least one material', 'error'); return Promise.resolve(false); }
    const body = { lines: payloadLines(), notes };
    return run('save', () => (mapping ? api.updateMapping(mapping.id, body) : api.saveMapping({ boq_item_id: boqItemId, ...body })),
               `Mapping saved as draft`);
  };
  const saveAndSubmit = async () => {
    if (dirty || !mapping) {
      const ok = await save();
      if (!ok) return;
    }
    const m = await api.getMappingForBoqLine(boqItemId).catch(() => null);
    if (m) await run('submit', () => api.submitMapping(m.id), 'Submitted for approval');
  };

  if (mapping === undefined) return <RNLLoader title="Opening mapping..." subtitle="Loading the BOQ line and its materials" />;

  return (
    <div>
      <HeaderBack onClick={() => goTo('register')}>Back to Mapping Register</HeaderBack>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{mapping ? `${mapping.mapping_ref}` : 'Create BOQ–Material Mapping'}</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Link this BOQ line to the stock materials needed to execute its work package.
          </p>
        </div>
        {mapping && (
          <div style={{ textAlign: 'right' }}>
            <StatusPill status={mapping.status} />
            {mapping.currently_with && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>with {mapping.currently_with}</div>}
          </div>
        )}
      </div>

      <Panel title="Mapping target">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 10 }}>
          <Select label="Building" value={target.division} onChange={v => setTarget(t => ({ ...t, division: v }))}
                  options={asOptions(lists.divisions, 'project_division_id', 'All buildings')} />
          <Select label="Discipline" value={target.category} onChange={v => setTarget(t => ({ ...t, category: v }))}
                  options={asOptions(lists.categories, 'id', 'All disciplines')} />
          <Select label="Work Package" value={target.subsection} onChange={v => setTarget(t => ({ ...t, subsection: v }))}
                  options={asOptions(lists.subsections, 'id', 'All work packages')} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>BOQ LINE</div>
        <SearchableSelect value={boqItemId} dropdownWidth={620}
                          onChange={v => { if (v && v !== boqItemId) openMapper(v); }}
                          options={boqOptions.map(o => ({ value: o.boq_item_id, label: `${o.boq_code} — ${o.description.slice(0, 90)}${o.mapping_ref ? `  [${o.mapping_ref}]` : ''}` }))} />
        {line && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 12 }}>
            <Field label="BOQ Code">{line.boq_code}</Field>
            <Field label="BOQ Qty">{fmtQty(line.quantity)} {line.unit_of_measure}</Field>
            <Field label="Building">{line.building}</Field>
            <Field label="Discipline">{line.discipline}</Field>
            <Field label="Work Package">{line.work_package}</Field>
            <Field label="BOQ">{line.boq_name}</Field>
            <div style={{ gridColumn: '1 / -1' }}><Field label="BOQ Description">{line.description}</Field></div>
          </div>
        )}
      </Panel>

      {editable && (
        <Panel title="Select materials (from the material master)"
               right={<span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{selected.length} selected</span>}>
          {suggestions.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                <Sparkles size={14} color="#7c3aed" /> Suggested for this BOQ line (matched on its description)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {suggestions.map(s => {
                  const on = selectedIds.has(s.material_id);
                  return (
                    <button key={s.material_id} onClick={() => toggleMaterial({ ...s, unit_of_measure: s.unit_of_measure })}
                            title={`${s.code} · ${s.category || ''} · match ${Math.round(s.score * 100)}%`}
                            style={{ fontSize: 11.5, padding: '5px 10px', borderRadius: 999, cursor: 'pointer', fontFamily: 'var(--font)',
                                     border: `1px solid ${on ? '#16a34a' : '#c4b5fd'}`, background: on ? '#dcfce7' : '#f5f3ff', color: on ? '#166534' : '#5b21b6' }}>
                      {on ? '✓ ' : '+ '}{s.name} <span style={{ opacity: 0.7 }}>({s.unit_of_measure})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <div style={{ flex: '2 1 280px' }}>
              <Input value={search} onChange={setSearch} placeholder="Search code, name or category…"
                     onKeyDown={e => { if (e.key === 'Enter') { setQuery(search.trim()); setPage(p => ({ ...p, skip: 0 })); } }} />
            </div>
            <Btn onClick={() => { setQuery(search.trim()); setPage(p => ({ ...p, skip: 0 })); }}><Search size={14} /> Search</Btn>
            <div style={{ flex: '1 1 200px' }}>
              <Select value={catFilter} onChange={v => { setCatFilter(v); setQuery(''); setSearch(''); setPage(p => ({ ...p, skip: 0 })); }}
                      options={[{ value: '', label: 'All categories' }, ...categories.map(c => ({ value: c, label: c }))]} />
            </div>
          </div>
          {materials === null ? <RNLLoader title="Loading materials..." subtitle="Material master" /> : (
            <>
              <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10, maxHeight: 380, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['', 'Material Code', 'Material', 'Category', 'UOM'].map(h => <th key={h} style={{ ...th, position: 'sticky', top: 0 }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {materials.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No materials match.</td></tr>}
                    {materials.map(m => {
                      const id = m.material_id || m.id;
                      const on = selectedIds.has(id);
                      return (
                        <tr key={id} onClick={() => toggleMaterial(m)} style={{ cursor: 'pointer', background: on ? '#ecfdf5' : undefined }}>
                          <td style={td}><input type="checkbox" checked={on} readOnly /></td>
                          <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{m.code}</td>
                          <td style={td}>{m.name}</td>
                          <td style={td}>{m.category || '—'}</td>
                          <td style={td}>{m.unit_of_measure}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 10 }}><Pagination total={matTotal} skip={page.skip} limit={page.limit} onChange={setPage} /></div>
            </>
          )}
        </Panel>
      )}

      <Panel title={editable ? 'Classify selected materials' : 'Mapped materials'}
             right={editable && selected.length > 0 && <Btn variant="secondary" onClick={autoAll}><Wand2 size={14} /> Auto-classify all</Btn>}>
        {selected.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {editable ? 'Pick materials above — suggestions first, or search the material master.' : 'No materials mapped to this BOQ line.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Material', 'UOM', 'Mapping Type', 'BOQ Coverage', 'Qty / BOQ Unit', 'Remarks', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {selected.map(s => (
                  <tr key={s.material_id}>
                    <td style={{ ...td, minWidth: 220 }}>
                      <div style={{ fontWeight: 700 }}>{s.name}</div>
                      <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{s.code}{s.category ? ` · ${s.category}` : ''}</div>
                    </td>
                    <td style={td}>{s.uom || '—'}</td>
                    <td style={{ ...td, minWidth: 170 }}>
                      {editable ? <Select value={s.mapping_type} onChange={v => setRow(s.material_id, 'mapping_type', v)} options={MAPPING_TYPES} />
                        : MAPPING_TYPES.find(t => t.value === s.mapping_type)?.label}
                    </td>
                    <td style={{ ...td, minWidth: 200 }}>
                      {editable ? <Select value={s.boq_coverage} onChange={v => setRow(s.material_id, 'boq_coverage', v)} options={COVERAGE} />
                        : COVERAGE.find(t => t.value === s.boq_coverage)?.label}
                    </td>
                    <td style={{ ...td, width: 120 }}>
                      {editable ? <Input type="number" min="0" step="any" value={s.quantity_per_unit} placeholder="optional"
                                         onChange={v => setRow(s.material_id, 'quantity_per_unit', v)} />
                        : fmtQty(s.quantity_per_unit)}
                    </td>
                    <td style={{ ...td, minWidth: 180 }}>
                      {editable ? <Input value={s.remarks} placeholder="optional" onChange={v => setRow(s.material_id, 'remarks', v)} /> : (s.remarks || '—')}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {editable && (
                        <>
                          <button title="Suggest type and coverage" onClick={() => { const a = autoClassify(s.name, line?.description || ''); setRow(s.material_id, 'mapping_type', a.mapping_type); setRow(s.material_id, 'boq_coverage', a.boq_coverage); }}
                                  style={{ background: 'none', border: '1px solid #c4b5fd', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#6d28d9', marginRight: 6 }}><Wand2 size={13} /></button>
                          <button title="Remove" onClick={() => toggleMaterial(s)}
                                  style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={13} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          {editable ? <Textarea label="Notes" value={notes} onChange={v => { setNotes(v); setDirty(true); }} rows={2} />
            : notes && <Field label="Notes">{notes}</Field>}
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--text-secondary)', margin: '10px 0 0' }}>
          <b>Qty / BOQ unit</b> is optional: how much of the material one unit of this BOQ line uses (e.g. 1.05 m of pipe per metre run with 5% waste).
          When set, Site Measurement compares what was used with what the installed quantity needs.
        </p>
      </Panel>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: 16 }}>
        {editable && (
          <>
            {mapping?.status === 'draft' && !mapping.workflow_instance_id && (
              <Btn variant="danger" loading={busy === 'delete'} onClick={() => run('delete', () => api.deleteMapping(mapping.id), 'Draft deleted').then(ok => ok && goTo('register'))}>
                Delete Draft
              </Btn>
            )}
            <Btn variant="secondary" loading={busy === 'save'} onClick={save} disabled={!dirty && !!mapping}><Plus size={14} /> Save Draft</Btn>
            <Btn loading={busy === 'submit'} onClick={saveAndSubmit} disabled={!selected.length}>
              {mapping?.status === 'returned' ? 'Save & Resubmit' : 'Save & Submit for Approval'}
            </Btn>
          </>
        )}
        {canEdit && mapping?.status === 'pending_approval' && (
          <Btn variant="secondary" loading={busy === 'recall'} onClick={() => run('recall', () => api.recallMapping(mapping.id))}>Recall to Draft</Btn>
        )}
        {canEdit && mapping?.status === 'approved' && (
          <Btn variant="secondary" loading={busy === 'reopen'} onClick={() => run('reopen', () => api.reopenMapping(mapping.id))}>Reopen to Change</Btn>
        )}
        {canApprove && (
          <>
            <Btn variant="danger" onClick={() => setModal('reject')}>Reject</Btn>
            <Btn variant="secondary" onClick={() => setModal('return')}>Return for Correction</Btn>
            <Btn variant="success" onClick={() => setModal('approve')}>Approve</Btn>
          </>
        )}
      </div>

      {mapping && (
        <Panel title="Approval history">
          {mapping.history.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Prepared by {mapping.prepared_by || '—'} on {fmtDate(mapping.date_prepared)}. Not sent for approval yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ fontSize: 12.5 }}>Prepared by <b>{mapping.prepared_by || '—'}</b> on {fmtDate(mapping.date_prepared)}</div>
              {mapping.history.map((h, i) => (
                <div key={i} style={{ fontSize: 12.5, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, minWidth: 130 }}>{EVENT_LABEL[h.event] || h.event}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{h.by || '—'} · {fmtDate(h.at)}</span>
                  {h.comment && <span style={{ fontStyle: 'italic' }}>“{h.comment}”</span>}
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {modal && (
        <CommentModal
          title={modal === 'approve' ? 'Approve mapping' : modal === 'return' ? 'Return for correction' : 'Reject mapping'}
          message={`${mapping.mapping_ref} · ${line?.boq_code || ''}`}
          required={modal !== 'approve'} danger={modal === 'reject'}
          confirmLabel={modal === 'approve' ? 'Approve' : modal === 'return' ? 'Return' : 'Reject'}
          onClose={() => setModal(null)}
          onConfirm={async (comment) => {
            const fn = { approve: api.approveMapping, return: api.returnMapping, reject: api.rejectMapping }[modal];
            const ok = await run(modal, () => fn(mapping.id, comment || undefined));
            if (ok) setModal(null);
          }}
        />
      )}
    </div>
  );
}
